import { useContext, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import {
  Block,
  Button,
  Column,
  ConfirmModal,
  DateCellComponent,
  DefaultTableFilterProps,
  DetailsIcon,
  FlexContainer,
  SoundNotificationContext,
  Span,
  Timer,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';

import { closeAcceptanceAct, getAcceptanceActBags } from '../api';
import { AcceptanceActDto, AcceptanceActStatus, ManifestOperationResultStatus } from './manifestAcceptance';
import { useManifestAcceptanceStore } from './useManifestAcceptanceStore';

const UNDECLARED_COLOR = '#8a5a00';
const LINK_COLOR = '#2b50d9';

type Params = {
  status: AcceptanceActStatus;
  onRefetch: () => void;
  onOpenDetails: (act: AcceptanceActDto) => void;
};

/** Таблица приёмок: открытые — сканирование и завершение, закрытые — карточка с мешками */
export const useAcceptanceActsSettingsTable = ({ status, onRefetch, onOpenDetails }: Params) => {
  const { t, i18n } = useTranslation();
  const { createNotification } = useNotificationsV2();
  const { onSoundError, onSoundSuccess } = useContext(SoundNotificationContext);
  const { handleChangeCurrentAcceptance, handleChangeBagsData } = useManifestAcceptanceStore();
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const [loadingCloseIds, setLoadingCloseIds] = useState<number[]>([]);
  const [isOpenCloseConfirm, setIsOpenCloseConfirm] = useState(false);
  const [isDisabledCloseConfirm, setIsDisabledCloseConfirm] = useState(true);
  const [selectedCloseActId, setSelectedCloseActId] = useState<number | null>(null);

  const notifySystemError = () => {
    createNotification({
      type: 'error',
      message: {
        title: t('common.systemError.title'),
        desc: t('common.systemError.desc'),
      },
    });
    onSoundError?.();
  };

  const handleOpenScanning = async (act: AcceptanceActDto) => {
    try {
      setLoadingIds((prev) => [...prev, act.id]);
      const res = await getAcceptanceActBags(act.id);
      handleChangeCurrentAcceptance({
        acceptanceActId: act.id,
        acceptanceNumber: act.number,
        manifestNumber: act.manifestNumber ?? null,
      });
      handleChangeBagsData(res.bags ?? []);
    } catch {
      notifySystemError();
    } finally {
      setLoadingIds((prev) => prev.filter((id) => id !== act.id));
    }
  };

  const handleOpenCloseConfirm = (actId: number) => {
    setSelectedCloseActId(actId);
    setIsDisabledCloseConfirm(true);
    setIsOpenCloseConfirm(true);
  };

  const handleCloseCloseConfirm = () => {
    setSelectedCloseActId(null);
    setIsDisabledCloseConfirm(false);
    setIsOpenCloseConfirm(false);
  };

  const { mutateAsync: onCloseAct } = useMutation({
    mutationKey: ['close-acceptance-act-table'],
    mutationFn: closeAcceptanceAct,
    onMutate: (actId) => {
      setLoadingCloseIds((prev) => [...prev, actId]);
    },
    onSettled: (_, __, actId) => {
      setLoadingCloseIds((prev) => prev.filter((id) => id !== actId));
    },
    onSuccess: (res) => {
      handleCloseCloseConfirm();

      if (res.status === ManifestOperationResultStatus.Success) {
        createNotification({
          type: 'success',
          message: {
            title: t('manifestAcceptance.notifications.actClosed.title'),
            desc: t('manifestAcceptance.notifications.actClosed.desc'),
          },
        });
        onSoundSuccess?.();
        onRefetch();
        return;
      }

      // Интеграция с SortCenter API упала, но локально приёмка уже закрыта — таблицу всё равно обновляем.
      if (res.status === ManifestOperationResultStatus.HasErrorApiIntegration) {
        createNotification({
          type: 'warning',
          message: {
            title: t('manifestAcceptance.notifications.apiIntegration.title'),
            desc: t('manifestAcceptance.notifications.apiIntegration.desc'),
          },
        });
        onRefetch();
        return;
      }

      createNotification({
        type: 'warning',
        message: {
          title: t('manifestAcceptance.notifications.operationError.title'),
          desc: t('manifestAcceptance.notifications.operationError.desc'),
        },
      });
      onSoundError?.();
    },
    onError: () => {
      handleCloseCloseConfirm();
      notifySystemError();
    },
  });

  const filters: DefaultTableFilterProps<AcceptanceActDto>[] = useMemo(
    () => [
      {
        type: 'search',
        id: 'number',
        name: 'Acceptance Number',
        label: t('manifestAcceptance.acceptanceActs.searchLabel'),
        variant: 'primary',
      },
    ],
    [t],
  );

  const isClosedTab = status === AcceptanceActStatus.Closed;

  const columns: Column<AcceptanceActDto>[] = useMemo(() => {
    // Клик по номеру «проваливается» в приёмку: открытая — сканирование, закрытая — карточка мешков.
    const openAct = (row: AcceptanceActDto) => (isClosedTab ? onOpenDetails(row) : handleOpenScanning(row));

    const base: Column<AcceptanceActDto>[] = [
      {
        header: t('manifestAcceptance.acceptanceActs.number'),
        accessor: 'number',
        textAlign: 'center',
        renderCell: (row) => (
          <span
            role="button"
            tabIndex={0}
            onClick={() => openAct(row)}
            onKeyDown={(e) => e.key === 'Enter' && openAct(row)}
            style={{ color: LINK_COLOR, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
          >
            {row.number}
          </span>
        ),
      },
      {
        header: t('tableHeader.manifestNumber'),
        accessor: 'manifestNumber',
        textAlign: 'center',
        renderCell: (row) => row.manifestNumber ?? t('manifestAcceptance.acceptanceActs.withoutManifest'),
      },
      {
        header: t('tableHeader.vehicleNumber'),
        accessor: 'vehicleNumber',
        textAlign: 'center',
      },
      {
        header: t('manifestAcceptance.acceptanceActs.bagsCount'),
        accessor: 'bagsCount',
        textAlign: 'center',
        renderCell: (row) => row.bagsCount ?? 0,
      },
      {
        header: t('manifestAcceptance.acceptanceActs.undeclaredBagsCount'),
        accessor: 'undeclaredBagsCount',
        textAlign: 'center',
        renderCell: (row) =>
          row.undeclaredBagsCount > 0 ? (
            <span style={{ color: UNDECLARED_COLOR, fontWeight: 600 }}>{row.undeclaredBagsCount}</span>
          ) : (
            0
          ),
      },
      {
        header: t('manifestAcceptance.acceptanceActs.openedBy'),
        accessor: 'openedByLogin',
        textAlign: 'center',
        renderCell: (row) => row.openedByLogin ?? '-',
      },
      {
        header: t('tableHeader.createdAt'),
        accessor: 'createdAt',
        textAlign: 'center',
        renderCell: (row) => <DateCellComponent justify="center" date={row.createdAt} zone="local" />,
      },
    ];

    if (isClosedTab) {
      base.push({
        header: t('manifestAcceptance.acceptanceActs.closeAt'),
        accessor: 'closeAt',
        textAlign: 'center',
        renderCell: (row) =>
          row.closeAt ? <DateCellComponent justify="center" date={row.closeAt} zone="local" /> : '-',
      });
    }

    base.push({
      header: t('tableHeader.actions'),
      accessor: 'id',
      textAlign: 'center',
      minWidth: isClosedTab ? '140px' : '300px',
      // Кнопки не растягиваем на всю ширину: две подписи в узкой колонке вылезали за край ячейки;
      // на узких экранах они переносятся на вторую строку.
      renderCell: (row) =>
        isClosedTab ? (
          <FlexContainer justify="center">
            <Button type="secondary" onClick={() => onOpenDetails(row)}>
              <FlexContainer align="center" gap="4px">
                <DetailsIcon cursor="pointer" size="xs" />
                {t('details')}
              </FlexContainer>
            </Button>
          </FlexContainer>
        ) : (
          <FlexContainer style={{ flexWrap: 'wrap' }} align="center" justify="center" gap="8px">
            <Button
              type="secondary"
              onClick={() => handleOpenScanning(row)}
              loading={loadingIds.includes(row.id)}
              disabled={loadingIds.includes(row.id)}
            >
              {t('manifestAcceptance.actions.scanning')}
            </Button>
            <Button
              onClick={() => handleOpenCloseConfirm(row.id)}
              loading={loadingCloseIds.includes(row.id)}
              disabled={loadingCloseIds.includes(row.id)}
            >
              {t('manifestAcceptance.actions.finish')}
            </Button>
          </FlexContainer>
        ),
    });

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosedTab, loadingCloseIds, loadingIds, onOpenDetails, t]);

  const closeConfirmModal = (
    <ConfirmModal
      onSubmit={() => selectedCloseActId !== null && onCloseAct(selectedCloseActId)}
      lang={i18n.language}
      desc={
        <FlexContainer direction="column" align="center" justify="center">
          <Block mb={8}>
            <Span>{t('manifestAcceptance.acceptanceActs.closeConfirm.desc')}</Span>
          </Block>
          <Timer onComplete={() => setIsDisabledCloseConfirm(false)} durationMs={5000} />
        </FlexContainer>
      }
      isOpen={isOpenCloseConfirm}
      disabled={isDisabledCloseConfirm}
      isLoading={selectedCloseActId !== null ? loadingCloseIds.includes(selectedCloseActId) : false}
      onClose={handleCloseCloseConfirm}
    />
  );

  return { columns, filters, closeConfirmModal, handleOpenScanning };
};
